import { useEffect, useRef, useState } from 'react';
import assistantService from '../../services/assistantService';
import { errText } from '../../services/scheduleService';
import useSpeechRecognition from '../../hooks/useSpeechRecognition';

const SPEAK_KEY = 'assistantSpeak';
const pad = (n) => String(n).padStart(2, '0');
const clientNow = () => {
  const d = new Date();
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
};
const readTurns = (key) => {
  if (!key) return [];
  try {
    return (JSON.parse(sessionStorage.getItem(key)) || []).filter((t) => !t.pending && !t.deciding);
  } catch {
    return [];
  }
};

/**
 * useAssistant — one conversation with the OmniPrac assistant.
 *
 * Shared by the Assistant page and the quick-access drawer on other pages.
 * persistKey keeps the conversation in sessionStorage so leaving the page and
 * coming back doesn't lose it (proposals still expire on the server).
 * billingContext { bid, patient_id, start, end } — set when the doctor pressed
 * Bill on a booking; sent with every turn so "Bill case" bills that patient.
 * quoteContext { bid, patient_id } — the same for "Quote with assistant"; a patient
 * picked from the assistant's list is sent with that one turn (choose).
 */
const useAssistant = ({ onExecuted, persistKey, billingContext = null, quoteContext = null } = {}) => {
  const [status, setStatus] = useState(null);
  const [turns, setTurns] = useState(() => readTurns(persistKey));
  const [busy, setBusy] = useState(false);
  const [speak, setSpeak] = useState(() => {
    try { return localStorage.getItem(SPEAK_KEY) === 'true'; } catch { return false; }
  });
  const sendRef = useRef(null);
  const onExecutedRef = useRef(onExecuted);
  onExecutedRef.current = onExecuted;
  const billingRef = useRef(billingContext);
  billingRef.current = billingContext;
  const quoteRef = useRef(quoteContext);
  quoteRef.current = quoteContext;

  const speech = useSpeechRecognition({ lang: 'en-ZA', onFinal: (text) => sendRef.current?.(text, 'voice') });

  useEffect(() => {
    assistantService.status()
      .then(setStatus)
      .catch((e) => setStatus({ ready: false, reason: errText(e), modules: [], procedures: [] }));
  }, []);

  useEffect(() => {
    if (!persistKey) return;
    try {
      sessionStorage.setItem(persistKey, JSON.stringify(turns.filter((t) => !t.pending).slice(-40)));
    } catch { /* storage full or blocked — conversation just isn't kept */ }
  }, [turns, persistKey]);

  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  const say = (text) => {
    if (!speak || !window.speechSynthesis || !text) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'en-ZA';
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };

  const toggleSpeak = () =>
    setSpeak((s) => {
      try { localStorage.setItem(SPEAK_KEY, String(!s)); } catch { /* preference only */ }
      if (s) window.speechSynthesis?.cancel();
      return !s;
    });

  const patchTurn = (id, patch) => setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  const send = async (raw, channel = 'text', opts = {}) => {
    const text = (raw || '').trim();
    if (!text || busy) return false;
    const id = Date.now();
    const history = turns.filter((t) => t.response?.plan).slice(-4).map((t) => ({ text: t.text, plan: t.response.plan }));
    setTurns((prev) => [...prev, { id, text, channel, pending: true }]);
    setBusy(true);
    try {
      const payload = { text, channel, client_now: clientNow(), history };
      if (billingRef.current) payload.billing_context = billingRef.current;
      const quoteCtx = opts.quoteContext || quoteRef.current;
      if (quoteCtx) payload.quote_context = quoteCtx;
      const response = await assistantService.interpret(payload);
      patchTurn(id, { pending: false, response });
      if (channel === 'voice') say(response.error ? `${response.reply} But: ${response.error}` : response.reply);
    } catch (e) {
      patchTurn(id, { pending: false, error: errText(e) });
    } finally {
      setBusy(false);
    }
    return true;
  };
  sendRef.current = send;

  // A booked patient picked from the assistant's list ("which patient is the quote for?").
  const choose = (choice) => send(choice.say || choice.label, 'text', { quoteContext: choice.context });

  const decide = async (turn, yes) => {
    const proposalId = turn.response.proposal.proposal_id;
    patchTurn(turn.id, { deciding: true });
    try {
      if (yes) {
        const r = await assistantService.confirm(proposalId);
        patchTurn(turn.id, { deciding: false, decision: 'confirmed', outcome: r.steps });
        onExecutedRef.current?.(r);
        if (turn.channel === 'voice') say('Done.');
      } else {
        await assistantService.cancel(proposalId);
        patchTurn(turn.id, { deciding: false, decision: 'cancelled' });
      }
    } catch (e) {
      patchTurn(turn.id, { deciding: false, decision: 'failed', decisionError: errText(e) });
    }
  };

  const reset = () => {
    window.speechSynthesis?.cancel();
    setTurns([]);
  };

  // A few example requests across whatever the assistant can currently do.
  const suggestions = (status?.modules || [])
    .flatMap((m) => m.procedures.flatMap((p) => p.examples.slice(0, 1)))
    .slice(0, 4);

  return { status, ready: !!status?.ready, turns, busy, send, choose, decide, reset, speak, toggleSpeak, speech, suggestions };
};

export default useAssistant;
